import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import * as Dialog from '@radix-ui/react-dialog';
import {
  X,
  Plus,
  Edit2,
  Trash2,
  Type,
  Settings,
  Eye,
  AlertTriangle,
  Zap
} from 'lucide-react';
import { api } from '../../../services/api';

interface TipoCampo {
  id: string;
  nome: string;
  tipo_input: string;
  validacao?: any;
  opcoes?: any[];
  placeholder?: string;
  descricao: string;
  ativo: boolean;
}

interface GrupoTipoAcaoCampo {
  id: string;
  campo: string;
  grupoTiposAcoes: {
    id: string;
    descricao: string;
  };
  camposTipo: TipoCampo;
  criado_em: string;
  alterado_em: string;
}

interface CampoAcaoModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  tipoAcaoId: string;
  tipoAcaoDescricao: string;
}

export function CampoAcaoModal({
  open,
  onClose,
  onSuccess,
  tipoAcaoId,
  tipoAcaoDescricao
}: CampoAcaoModalProps) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Estados principais
  const [campos, setCampos] = useState<GrupoTipoAcaoCampo[]>([]);
  const [tiposCampos, setTiposCampos] = useState<TipoCampo[]>([]);
  const [loading, setLoading] = useState(false);

  // Estados do formulário
  const [showForm, setShowForm] = useState(false);
  const [editingCampo, setEditingCampo] = useState<GrupoTipoAcaoCampo | null>(null);
  const [formData, setFormData] = useState({
    campo: '',
    id_tipo_campo: ''
  });



  useEffect(() => {
    if (open) {
      loadCampos();
      loadTiposCampos();
    }
  }, [open, tipoAcaoId]);

  const loadCampos = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/grupos-tipos-acoes-campos/tipo-acao/${tipoAcaoId}`);
      setCampos(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar campos:', error);
      enqueueSnackbar('Erro ao carregar campos', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const loadTiposCampos = async () => {
    try {
      const response = await api.get('/campos-tipos');
      setTiposCampos(response.data.filter((tipo: TipoCampo) => tipo.ativo));
    } catch (error: any) {
      console.error('Erro ao carregar tipos de campos:', error);
      enqueueSnackbar('Erro ao carregar tipos de campos', { variant: 'error' });
    }
  };

  const handleOpenForm = (campo?: GrupoTipoAcaoCampo) => {
    if (campo) {
      setEditingCampo(campo);
      setFormData({
        campo: campo.campo,
        id_tipo_campo: campo.camposTipo.id
      });
    } else {
      setEditingCampo(null);
      setFormData({
        campo: '',
        id_tipo_campo: ''
      });
    }
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCampo(null);
    setFormData({
      campo: '',
      id_tipo_campo: ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.campo.trim()) {
      enqueueSnackbar('Nome do campo é obrigatório', { variant: 'error' });
      return;
    }

    if (!formData.id_tipo_campo) {
      enqueueSnackbar('Tipo do campo é obrigatório', { variant: 'error' });
      return;
    }

    setLoading(true);
    try {
      if (editingCampo) {
        await api.put(`/grupos-tipos-acoes-campos/${editingCampo.id}`, formData);
        enqueueSnackbar('Campo atualizado com sucesso!', { variant: 'success' });
      } else {
        await api.post('/grupos-tipos-acoes-campos', {
          ...formData,
          id_grupo_tipo_acao: tipoAcaoId
        });
        enqueueSnackbar('Campo criado com sucesso!', { variant: 'success' });
      }

      await loadCampos();
      handleCloseForm();
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao salvar campo',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (campo: GrupoTipoAcaoCampo) => {
    const snackbarKey = enqueueSnackbar(`Excluir campo "${campo.campo}"?`, {
      variant: 'default',
      persist: true,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      style: {
        backgroundColor: '#dc2626', // bg-red-600
        color: 'white',
        fontWeight: '500',
        minWidth: '400px',
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999
      },
      action: (key) => (
        <div className="flex space-x-3 ml-4">
          <button
            onClick={() => {
              closeSnackbar(key);
              handleConfirmDelete(campo);
            }}
            className="px-4 py-2 text-sm font-medium bg-red-800 text-white rounded-md hover:bg-red-900 transition-colors"
          >
            Excluir
          </button>
          <button
            onClick={() => closeSnackbar(key)}
            className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Cancelar
          </button>
        </div>
      ),
    });
  };

  const handleConfirmDelete = async (campo: GrupoTipoAcaoCampo) => {
    setLoading(true);
    try {
      await api.delete(`/grupos-tipos-acoes-campos/${campo.id}`);
      enqueueSnackbar('Campo excluído com sucesso!', { variant: 'success' });
      await loadCampos();
      onSuccess();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao excluir campo',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const getTipoCampoNome = (tipoCampoId: string) => {
    const tipo = tiposCampos.find(t => t.id === tipoCampoId);
    return tipo?.nome || 'Não encontrado';
  };



  return (
    <>
      <Dialog.Root open={open} onOpenChange={onClose}>
        <Dialog.Portal>
          <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50" />
          <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-50 grid w-full max-w-4xl translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-background text-foreground p-6 shadow-lg duration-200 rounded-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <Settings className="w-6 h-6 text-blue-500" />
                <div>
                  <Dialog.Title className="text-xl font-semibold text-foreground">
                    Configurar Campos
                  </Dialog.Title>
                  <p className="text-sm text-muted-foreground">
                    Tipo de Ação: {tipoAcaoDescricao}
                  </p>
                </div>
              </div>
              <Dialog.Close className="rounded-md p-2 text-muted-foreground hover:text-foreground hover:bg-muted">
                <X size={18} />
              </Dialog.Close>
            </div>

            {!showForm ? (
              <>
                {/* Header com botão adicionar */}
                <div className="flex items-center justify-between mb-4">
                  <p className="text-muted-foreground">
                    {campos.length} campo{campos.length !== 1 ? 's' : ''} configurado{campos.length !== 1 ? 's' : ''}
                  </p>
                  <button
                    onClick={() => handleOpenForm()}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    disabled={loading}
                  >
                    <Plus size={16} />
                    <span>Adicionar Campo</span>
                  </button>
                </div>

                {/* Lista de campos */}
                {loading ? (
                  <div className="py-8 text-center">
                    <div className="text-muted-foreground">Carregando campos...</div>
                  </div>
                ) : campos.length === 0 ? (
                  <div className="py-8 text-center">
                    <Type className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      Nenhum campo configurado
                    </h3>
                    <p className="text-muted-foreground mb-4">
                      Adicione campos para customizar as informações coletadas neste tipo de ação.
                    </p>
                    <button
                      onClick={() => handleOpenForm()}
                      className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                    >
                      <Plus size={16} />
                      <span>Adicionar Primeiro Campo</span>
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {campos.map((campo) => (
                      <div key={campo.id} className="border border-border rounded-lg p-4 hover:bg-muted transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <Type className="w-5 h-5 text-blue-500" />
                              <h3 className="font-medium text-foreground">{campo.campo}</h3>
                              <span className="px-2 py-1 text-xs font-medium bg-muted text-foreground rounded-full">
                                {campo.camposTipo.nome}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground">{campo.camposTipo.descricao}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Criado em {new Date(campo.criado_em).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleOpenForm(campo)}
                              className="p-2 text-blue-500 hover:bg-muted rounded-md transition-colors"
                              title="Editar campo"
                              disabled={loading}
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(campo)}
                              className="p-2 text-red-500 hover:bg-muted dark:hover:bg-red-900/20 rounded-md transition-colors"
                              title="Excluir campo"
                              disabled={loading}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              /* Formulário */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-foreground">
                    {editingCampo ? 'Editar Campo' : 'Novo Campo'}
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome do Campo *
                  </label>
                  <input
                    type="text"
                    value={formData.campo}
                    onChange={(e) => setFormData(prev => ({ ...prev, campo: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ex: Observações, Local, Duração..."
                    required
                    maxLength={100}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.campo.length}/100 caracteres
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tipo do Campo *
                  </label>
                  <select
                    value={formData.id_tipo_campo}
                    onChange={(e) => setFormData(prev => ({ ...prev, id_tipo_campo: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Selecione o tipo do campo</option>
                    {tiposCampos.map((tipo) => (
                      <option key={tipo.id} value={tipo.id}>
                        {tipo.nome} - {tipo.descricao}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Preview do campo selecionado */}
                {formData.id_tipo_campo && (
                  <div className="border border-border rounded-lg p-4 bg-muted/50">
                    <div className="flex items-center space-x-2 mb-2">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Preview</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Tipo: {getTipoCampoNome(formData.id_tipo_campo)}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseForm}
                    className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-muted"
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Salvando...' : editingCampo ? 'Atualizar' : 'Criar'}
                  </button>
                </div>
              </form>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  );
}
